<template>
  <div class="qb-row" :class="[{ 'qb-disabled': isZeroMax }, sizeClass]">
    <div
      class="qb-track"
      :aria-label="aria"
      role="progressbar"
      :aria-valuemin="0"
      :aria-valuenow="clamped"
      :aria-valuemax="max"
      :title="tooltip"
    >
      <div class="qb-fill" :class="fillClass" :style="{ width: fillWidth }" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'

  const props = withDefaults(
    defineProps<{
      value: number | null | undefined
      max: number | null | undefined
      size?: 'sm' | 'md' | 'lg'
      ariaLabel?: string
      showTooltip?: boolean
    }>(),
    {
      size: 'md',
      showTooltip: false,
    },
  )

  const asNum = (n: number | null | undefined) => (typeof n === 'number' && isFinite(n) ? n : 0)

  const max = computed(() => Math.max(0, asNum(props.max)))
  const value = computed(() => Math.max(0, asNum(props.value)))
  const isZeroMax = computed(() => max.value === 0)
  const pctNum = computed(() => (isZeroMax.value ? 0 : (value.value / max.value) * 100))
  const clamped = computed(() => Math.min(value.value, max.value))
  const over = computed(() => !isZeroMax.value && value.value > max.value)

  const fillWidth = computed(() => {
    if (isZeroMax.value) return '0%'
    return `${Math.min(100, (value.value / max.value) * 100)}%`
  })

  const fillClass = computed(() => {
    const p = Math.round(Math.min(1000, Math.max(0, pctNum.value)))
    if (over.value) return 'qb-crit qb-over'
    if (p < 60) return 'qb-ok'
    if (p < 85) return 'qb-warn'
    return 'qb-crit'
  })

  const sizeClass = computed(() => `qb-${props.size}`)
  const aria = computed(() => props.ariaLabel ?? 'quota usage')
  const tooltip = computed(() => (props.showTooltip && !isZeroMax.value ? `${Math.round(pctNum.value)}%` : ''))
</script>

<style scoped>
  :host,
  .qb-row {
    --qb-track: #e8eaed;
    --qb-fill: #0ea5e9;
    --qb-text: #0b0f19;
    --qb-ok: #07a287;
    --qb-warn: #f59e0b;
    --qb-crit: #ef4444;
    --qb-muted: #a1a1aa;
  }

  .qb-row {
    display: block;
  }

  .qb-track {
    position: relative;
    height: 8px;
    background: var(--qb-track);
    border-radius: 6px;
    overflow: hidden;
    outline: none;
  }
  .qb-track:hover {
    filter: brightness(1.02);
  }

  .qb-fill {
    position: absolute;
    inset: 0 auto 0 0;
    background: var(--qb-fill);
    border-radius: 6px;
    transition: width 220ms ease;
  }
  .qb-fill.qb-ok {
    background: var(--qb-ok);
  }
  .qb-fill.qb-warn {
    background: var(--qb-warn);
  }
  .qb-fill.qb-crit {
    background: var(--qb-crit);
  }
  .qb-fill.qb-over {
    box-shadow: inset -6px 0 0 0 rgba(255, 255, 255, 0.45);
  }

  .qb-sm .qb-track {
    height: 10px;
  }
  .qb-lg .qb-track {
    height: 10px;
  }
  .qb-disabled .qb-track {
    opacity: 0.5;
  }
</style>
